import Head from "next/head";
import { useRouter } from "next/router";

import DesktopNavigation from "@musicfy/components/Navigation/DesktopNavigation";
import MobileNavigation from "@musicfy/components/Navigation/MobileNavigation";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  FAVICON_URL,
  PageMeta,
  getRobotsForPath,
  organizationJsonLd,
  websiteJsonLd,
} from "@musicfy/components/Seo";
import {
  buildVoicePageTitle,
  getVoiceCharacterNameFromPath,
} from "@musicfy/utils/voicePageSeo";

import { APPS, type IAppInfo, findActiveSection } from "./constants";

const Navigation: React.FC<{
  toggleNavPinned: () => void;
  isNavPinned: boolean;
}> = ({ toggleNavPinned, isNavPinned }) => {
  const router = useRouter();

  const currentAppRoute = router.pathname.split("/")[1];
  const activeApp = !currentAppRoute
    ? undefined
    : APPS.find(
        ({ route }) =>
          currentAppRoute === route.split("/")[1] && !route.includes("http")
      ) ?? (APPS[0] as IAppInfo);

  const activeSection = !activeApp
    ? undefined
    : findActiveSection(router.asPath, activeApp.features);

  const isVoicePage = router.pathname.includes("[...voice]");
  const voiceCharacter = isVoicePage
    ? getVoiceCharacterNameFromPath(router.asPath)
    : "";
  const isVoiceModelPage = isVoicePage && voiceCharacter !== "";

  const pageTitle = isVoicePage
    ? buildVoicePageTitle(voiceCharacter)
    : activeSection?.displayName
      ? `Musicfy AI - ${activeSection.displayName}`
      : DEFAULT_TITLE;

  const description = activeSection?.subtitle || DEFAULT_DESCRIPTION;

  const pathname = router.pathname.split("?")[0] ?? router.pathname;
  const robots = getRobotsForPath(pathname);
  const canonicalPath =
    (router.asPath.split("?")[0] ?? "/").replace(/\/$/, "") || "/";

  const isPublicIndexable = robots === "index,follow";

  return (
    <nav>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>
      {/* Voice model pages set richer meta in the page component. */}
      {!isVoiceModelPage ? (
        <PageMeta
          title={pageTitle}
          description={description}
          canonicalPath={canonicalPath}
          robots={robots}
          jsonLd={
            isPublicIndexable
              ? [organizationJsonLd(), websiteJsonLd()]
              : undefined
          }
        />
      ) : (
        <Head>
          <link rel="icon" href={FAVICON_URL} sizes="any" />
        </Head>
      )}
      <div className="block xl:hidden">
        <MobileNavigation />
      </div>

      <DesktopNavigation
        toggleNavPinned={toggleNavPinned}
        isNavPinned={isNavPinned}
      />
    </nav>
  );
};

export default Navigation;
